import type { RunProjection } from "@roboscientist/contracts";
import { isRunningRun } from "./runTiming.js";
import type { BrokenTimelineScale } from "./timelineBreaks.js";

export const runningRunDecal = (run: RunProjection, color: string) => isRunningRun(run) ? {
  symbol: "rect",
  symbolSize: 0.7,
  color,
  backgroundColor: "transparent",
  dashArrayX: [1, 0],
  dashArrayY: [3, 3],
  rotation: -Math.PI / 4
} : undefined;

export const progressUnit = (run: RunProjection): string => run.progress.unit || "step";

export const progressUnitLabel = (run: RunProjection): string => progressUnit(run).toUpperCase();

export const progressValue = (run: RunProjection): string => run.progress.current == null
  ? "—"
  : `${run.progress.current} / ${run.progress.total ?? "?"}`;

export const progressStageValue = (run: RunProjection): string | null => {
  if (!run.progress.stage) return null;
  const position = run.progress.stageCurrent == null
    ? ""
    : ` · ${run.progress.stageCurrent} / ${run.progress.stageTotal ?? "?"}`;
  return `${run.progress.stage}${position}`;
};

export const timelineTimeAtPosition = ({
  minimum,
  maximum,
  zoomStart,
  zoomEnd,
  position,
  width
}: {
  minimum: number;
  maximum: number;
  zoomStart: number;
  zoomEnd: number;
  position: number;
  width: number;
}): number => {
  const extent = Math.max(1, maximum - minimum);
  const visibleStart = minimum + extent * Math.max(0, Math.min(100, zoomStart)) / 100;
  const visibleEnd = minimum + extent * Math.max(0, Math.min(100, zoomEnd)) / 100;
  const ratio = Math.max(0, Math.min(1, position / Math.max(1, width)));
  return visibleStart + Math.max(1, visibleEnd - visibleStart) * ratio;
};

/** Use the same pixel scale as the replay cursor, including compressed gaps. */
export const timelineBarGeometry = (
  scale: BrokenTimelineScale,
  start: number,
  end: number,
  cursorTime?: number
): { start: number; width: number } => {
  const clippedStart = Math.max(scale.domainStart, start);
  const clippedEnd = Math.min(scale.domainEnd, end, cursorTime ?? Infinity);
  const x = scale.toX(clippedStart);
  if (clippedEnd <= clippedStart) return { start: x, width: 0 };
  const width = scale.toX(clippedEnd) - x;
  // A minimum 1 px width can put a newly started bar into the future.
  // Preserve subpixel widths during replay; ordinary timelines retain the marker.
  return { start: x, width: Math.max(cursorTime == null ? 1 : 0, width) };
};
